import styled from '@emotion/styled';
import { ThemeProvider, useTheme } from '@emotion/react';
import { StyledModalComponent } from '@dnhb/node_package';
import { useState } from 'react';
import { PrimaryButton, RoundedButton } from 'shared/buttons';
import useMedia from 'shared/hooks/useMedia';
import { SelectInput, ToggleSwitch } from 'shared/inputs';
import { Heading, LabelText } from 'shared/components';
import LabelSvg from 'images/renteoverzicht/label.svg';

const monthsInYear = 12;

const TableHeaderContainer = styled.div`
  display: flex;
  height: 84px;
  justify-items: center;
  align-items: center;
`;

const ToggleCollabContainer = styled.div`
  flex: 280px 3 1;
`;

const CollabSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding-right: 8px;

  > div {
    margin: 0 0 0 12px;
  }

  img {
    margin: 0 8px;
    height: 24px;
    vertical-align: middle;
  }

  svg {
    display: inline-block;
    height: 20px;
    width: 20px;
    margin: 0 8px;
    vertical-align: middle;
    fill: ${({ theme }) => theme.highlightColor};
  }

  @media ${({ theme }) => theme.mobile} {
    img {
      margin: 0 12px;
    }
  }
`;

const Text = styled.div(props => ({
  color: props.theme.primaryFontColor,
  margin: '14px 0 26px 0',
  fontSize: '14px',
  fontFamily: props.theme.fontFamily,
}));

const Label = styled.div(props => ({
  marginTop: '24px',
  marginBottom: '8px',
  color: props.theme.primaryFontColor,
}));

const EditFrtButton = styled.div(props => ({
  flex: '152px 1 0',
  display: 'flex',
  justifyContent: 'center',
  button: {
    display: 'block',
    fontSize: '14px',
    textDecoration: 'underline',
    color: props.theme.primaryColor,
    cursor: 'pointer',
  },
}));

const CollabSwitch = ({ prefer_collaboration, changeSorting, isFlexFront }) => {
  const { highlightColor } = useTheme();

  return (
    <CollabSwitchContainer>
      <ThemeProvider theme={isFlexFront ? { primaryColor: highlightColor } : {}}>
        <ToggleSwitch
          name="prefer_collaboration"
          value={prefer_collaboration}
          onChange={e => changeSorting('prefer_collaboration', e.target.value)}
        />
      </ThemeProvider>
      <LabelText>
        Toon
        {isFlexFront ? (
          <img
            src={'https://www.hypotheekbond.nl/images/logo/logo-flexfront-transparent.png'}
            alt="FlexFront"
            title="FlexFront"
          />
        ) : (
          <>
            {' '}
            onze producten <LabelSvg />
          </>
        )}
        bovenaan
      </LabelText>
    </CollabSwitchContainer>
  );
};

const TableHeader = props => {
  const {
    changeSorting,
    settings,
    input: { frt, sorted_frt, prefer_collaboration },
    sync,
  } = props;
  const { includeProvidersWithOwnProduct, isFlexFront } = settings;
  const [isOpen, setOpen] = useState(false);
  const { onMobile } = useMedia();

  const [tempRvp, updateTempRvp] = useState([
    settings.firstProduct.years * monthsInYear,
    settings.secondProduct.years * monthsInYear,
    settings.thirdProduct.years * monthsInYear,
    settings.fourthProduct.years * monthsInYear,
    settings.fifthProduct.years * monthsInYear,
  ]);

  if (onMobile) {
    return (
      <div css={{ marginBottom: '24px' }}>
        {includeProvidersWithOwnProduct && (
          <div
            css={theme => ({
              background: theme.secondaryColor,
              padding: '16px',
              margin: '16px 0',
              borderRadius: '10px',
              fontSize: '16px',
              display: 'flex',
              justifyContent: 'center',
            })}
          >
            <ThemeProvider theme={{ disabledBackgroundColor: '#bbbbbb' }}>
              <CollabSwitch {...{ prefer_collaboration, changeSorting, isFlexFront }} />
            </ThemeProvider>
          </div>
        )}
        <Label>Selecteer een Rentevasteperiode</Label>
        <SelectInput
          placeholder="Rentevasteperiode"
          value={sorted_frt}
          options={rvpOptions()}
          onChange={change => {
            const { value } = change;
            const temp = [...tempRvp];
            const indexOf = temp.indexOf(value);
            if (indexOf > 0) {
              temp.splice(indexOf, 1, temp[0]);
            }
            temp.splice(0, 1, value);
            updateTempRvp(temp);
            sync('frt', temp);
            changeSorting('sorted_frt', temp[0]);
          }}
        />
      </div>
    );
  }

  return (
    <TableHeaderContainer>
      <ToggleCollabContainer>
        {includeProvidersWithOwnProduct && <CollabSwitch {...{ prefer_collaboration, changeSorting, isFlexFront }} />}
      </ToggleCollabContainer>
      {frt.map((option, index) => (
        <div key={option + index} css={{ flex: '64px 1 0' }}>
          <RoundedButton
            active={sorted_frt === option}
            onClick={() => changeSorting('sorted_frt', option)}
            css={{
              minWidth: '64px',
              width: '64px',
              margin: '0 auto',
              padding: '6px 8px',
            }}
          >
            {option === 0 ? 'Variabel' : `${option / monthsInYear} jaar`}
          </RoundedButton>
        </div>
      ))}

      <EditFrtButton>
        <button onClick={() => setOpen(true)}>Wijzigen</button>
      </EditFrtButton>

      <StyledModalComponent
        appElement={document.getElementById('hypotheekbespaarcheck-app')}
        isOpen={isOpen}
        onRequestClose={() => setOpen(false)}
        maxWidth={540}
        overflowY="visible"
      >
        <ThemeProvider theme={{ cardColor: '#ffffff' }}>
          <Heading css={{ textAlign: 'left' }}>Rentevasteperiode aanpassen</Heading>
          <Text>Kies hieronder de verschillende looptijden die u wilt vergelijken.</Text>
          <div css={{ marginTop: '12px', marginBottom: '24px' }}>
            {frt.map((option, index) => (
              <div key={index} css={{ marginTop: '12px' }}>
                <SelectInput
                  placeholder="Rentevasteperiode"
                  value={formatSelectValue(tempRvp, index)}
                  options={rvpOptions(index, tempRvp)}
                  onChange={change => {
                    const temp = [...tempRvp];
                    temp.splice(index, 1, change.value);
                    updateTempRvp(temp);
                  }}
                />
              </div>
            ))}
          </div>
          <PrimaryButton
            onClick={() => {
              sync('frt', tempRvp);
              changeSorting('sorted_frt', tempRvp[0]);
              setOpen(false);
            }}
          >
            Toepassen
          </PrimaryButton>
        </ThemeProvider>
      </StyledModalComponent>
    </TableHeaderContainer>
  );
};

TableHeader.propTypes = {};
TableHeader.defaultProps = {};

export default TableHeader;

function formatSelectValue(frt, index) {
  const obj = {};
  obj.value = frt[index];
  if (obj.value === 0) {
    obj.label = 'Variabel';
  } else {
    obj.label = `${obj.value / 12} jaar`;
  }
  return obj.value;
}

function rvpOptions(index, tempRvp) {
  let i;
  const options = [{ label: 'Variabel', value: 0 }];
  for (i = 1; i < 31; i += 1) {
    const year = i;
    const months = i * monthsInYear;
    options.push({ label: `${year} jaar`, value: months });
  }

  let availableOptions;

  if (index) {
    const otherOptions = [...tempRvp];
    otherOptions.splice(index, 1);

    availableOptions = options.filter(opt => !otherOptions.includes(opt.value));
  } else {
    availableOptions = options;
  }

  return availableOptions;
}
